import { useEffect, useMemo, useState } from "react"
import { Alert, Spin } from "antd"
import { Outlet, useLocation } from "react-router-dom"
import { requestToken } from "@/auth/AuthServices"
import { setSapPostLoginRedirect } from "../utils/sapPostLoginRedirect"

type GuardStatus = "checking" | "ready" | "error"

const SapAuthGuard = () => {
  const location = useLocation()
  const [status, setStatus] = useState<GuardStatus>("checking")

  const currentPath = useMemo(
    () => `${location.pathname}${location.search}${location.hash}`,
    [location.hash, location.pathname, location.search],
  )

  useEffect(() => {
    let isMounted = true

    const ensureSapSession = async () => {
      try {
        const existingToken = await requestToken(false)

        if (existingToken) {
          if (isMounted) {
            setStatus("ready")
          }
          return
        }

        setSapPostLoginRedirect(currentPath)
        await requestToken(true)

        if (isMounted) {
          setStatus("checking")
        }
      } catch (error) {
        console.error("SAP auth bootstrap failed:", error)

        if (isMounted) {
          setStatus("error")
        }
      }
    }

    void ensureSapSession()

    return () => {
      isMounted = false
    }
  }, [currentPath])

  if (status === "ready") {
    return <Outlet />
  }

  if (status === "error") {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "48px 16px" }}>
        <Alert
          type="error"
          showIcon
          message="Authentication failed"
          description="Unable to initialize SAP access. Please refresh and try again."
        />
      </div>
    )
  }

  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh", padding: "48px 16px" }}>
      <div style={{ textAlign: "center" }}>
        <Spin size="large" />
        <div style={{ marginTop: 16, color: "#8c8c8c" }}>Checking SAP access...</div>
      </div>
    </div>
  )
}

export default SapAuthGuard
